import type { PortalData } from '../generated/api';
import config from '../config';
import LocalStorage from './LocalStorage';
import { isEmbeddedFrame } from './frameScope';

export const PORTAL_DATA_KEY = 'portal-data';

async function warm(tenantId?: string, opts?: { transient?: boolean }) {
  const query = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  const url = `${config.PROTOCOL}://${config.API_URL}/api/portal-data${query}`;
  let data: PortalData | undefined = undefined;

  try {
    const res = await fetch(url);
    if (res.status !== 200 && res.status !== 304) {
      throw new Error();
    }
    data = await res.json();
  } catch (err) {
    return;
  }

  // transient = the branding belongs to the session, not the host (generic
  // portal where the user picked the tenant at login) — wiped on logout.
  // Only the ensurePortalData login path sets it: a tenantId alone doesn't
  // imply transience (e.g. a dev build pinning warm('<tenant>') at boot).
  LocalStorage.set(
    LocalStorage.Keys.PortalData,
    opts?.transient ? { ...data, transient: true } : data,
  );

  // theme.js booted from the cached copy; re-apply with the fresh theme.
  // Unconditional (null resets to defaults) so switching to a theme-less
  // tenant doesn't keep serving the previous tenant's colors.
  (window as any).applyTheme?.(data?.theme ?? null);
  // same for the loading screen's brand mark (visible during the warmup gate)
  (window as any).applyLoadingMark?.(data ?? null);

  return data;
}

(window as any).careand_warmup = warm();

export const getPortalData = () => LocalStorage.get(LocalStorage.Keys.PortalData);

/**
 * Host-based warmup can miss (e.g. localhost, where the referer hostname
 * matches no tenant). Once a tenantId is known (decoded JWT after login),
 * re-fetch by ?tenant_id= — also covers stored data from a different tenant.
 */
export async function ensurePortalData(tenantId: string) {
  const warmup = (window as any).careand_warmup;
  if (warmup instanceof Promise) await warmup;

  const current = getPortalData();
  if (current?.tenant_id === tenantId) return current;
  return warm(tenantId, { transient: true });
}

/** Tenant jurisdiction province — the default for province fields (health
 * cards, referrals). Falls back to the legacy default when unset. */
export const getTenantProvince = () => getPortalData()?.province || 'Ontario';

/**
 * Patient-facing label for the provincial health-card number — "OHIP" for
 * Ontario tenants, generic elsewhere. Mirrors the backend's
 * tenantFeatures.jurisdiction.healthCardLabel() so a document, an email and
 * the screen a patient is reading all call the same thing by the same name.
 *
 * This is the LABEL only. Card-number format rules are province-specific
 * validation and live in ValidatorUtils.
 */
export const getHealthCardLabel = () =>
  getTenantProvince() === 'Ontario' ? 'OHIP' : 'Health Card';

/** Label for a provider's public-insurance billing number. Mirrors
 * tenantFeatures.jurisdiction.billingNumberLabel(). */
export const getBillingNumberLabel = () =>
  getTenantProvince() === 'Ontario' ? 'OHIP' : 'Billing #';

/** Label for a provider's college registration number — "CNO" (College of
 * Nurses of Ontario) for nursing types in Ontario, generic elsewhere. Mirrors
 * tenantFeatures.jurisdiction.collegeLabel(). */
export const getCollegeLabel = (physicianType?: string) =>
  getTenantProvince() === 'Ontario' &&
  (!physicianType || physicianType === 'Nurse Practitioner' || physicianType === 'Nurse')
    ? 'CNO'
    : 'Registration #';

/**
 * Client-side analytics (Matomo) config for this tenant, or undefined when the
 * tenant has no analytics module configured — which is also the "don't track"
 * signal. Server URL, site id and first-party domains all come from the tenant
 * record, so a tenant reports into its own Matomo instance.
 */
export const getAnalyticsConfig = () => getPortalData()?.analytics;

/**
 * On a generic portal (host resolves to no tenant; the user picked the tenant
 * at login) the cached portal-data is session branding, not host branding —
 * logout wipes it and resets the theme so the next visitor gets the generic
 * portal, not the previous user's tenant. Host-resolved portal-data survives.
 */
export function clearPortalDataIfTransient() {
  // frames share the parent portal's localStorage — never wipe it from a frame
  if (isEmbeddedFrame) return;
  const current = getPortalData();
  if (!current?.transient) return;
  LocalStorage.remove(LocalStorage.Keys.PortalData);
  (window as any).applyTheme?.(null);
}
